/**
 * Why a programme cannot be opened for editing from the programmes list.
 *
 * Mirrors the editor's own read-only rule, so a disabled Edit names the same
 * reason the editor would have shown on arrival.
 *
 * @module components/programmes/programme-edit-refusal
 */

import type { ProgrammeSummary, SessionContext } from "@/platform";
import { editorAvailability, VIEW_ONLY_ROLE } from "@/router/programme-editor/editor-availability";
import { can } from "@/server/authorization";
import { isWorkflowEditable } from "@/server/workflow";

/** The reason Edit is refused, or nothing when the account may edit. */
export function programmeEditRefusal(
  session: SessionContext,
  programme: Pick<ProgrammeSummary, "id" | "workflowState" | "visibilityState">,
): string | undefined {
  if (!isWorkflowEditable(programme.workflowState)) {
    return editorAvailability({
      readOnly: true,
      workflowState: programme.workflowState,
      canEditWorkflowState: false,
      canEditProgramme: false,
    })?.full;
  }
  if (programme.visibilityState === "locked") {
    return "This programme is locked, so it is read only.";
  }
  // `programme:write` admits a builder, who edits their modules from here.
  if (!can(session.subject, "programme:write", { type: "programme", programmeId: programme.id })) {
    return VIEW_ONLY_ROLE.full;
  }
  return undefined;
}
